import {Injectable, inject} from '@angular/core';
import {HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, catchError, throwError} from 'rxjs';
import {environment} from '../../environments/environment.development';
import {SERVER_RESPONSE} from '../server-response.token';
import {ApiAvailabilityService} from '../services/api-availability.service';

/**
 * Mark a request as a side widget (e.g. crypto ticker fed by external CoinGecko): its failure says nothing
 * about our site being overloaded and must not turn the whole page into 503.
 */
export const SSR_OPTIONAL = new HttpContextToken<boolean>(() => false);

/** Seconds a crawler/visitor should wait before retrying an overloaded page. */
export const RETRY_AFTER_SECONDS = 60;

/**
 * Server only (app.config.server.ts), #1174: our API answered 429/5xx or was unreachable while
 * rendering — the page would go out empty with 200 and a crawler would index it. Answer 503 +
 * Retry-After, forbid caching, and let the page show «перегрузка, обновите через минуту».
 * 4xx like 404 are normal answers and stay untouched.
 */
@Injectable()
export class SsrUnavailableInterceptor implements HttpInterceptor {
  private readonly response = inject(SERVER_RESPONSE, {optional: true});
  private readonly availability = inject(ApiAvailabilityService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(environment.apiBase) || request.context.get(SSR_OPTIONAL)) {
      return next.handle(request);
    }
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && (err.status === 0 || err.status === 429 || err.status >= 500)) {
          this.markPageUnavailable();
        }
        return throwError(() => err);
      })
    );
  }

  private markPageUnavailable(): void {
    this.availability.markUnavailable();
    this.response?.status(503);
    this.response?.setHeader?.('Retry-After', String(RETRY_AFTER_SECONDS));
    this.response?.setHeader?.('Cache-Control', 'no-store');
  }
}
